import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import { displayCell } from "@/lib/sheet-engine";
import type { BlockType, DocBlock, ModuleKey, OfficeState } from "@/types/office";

export type StepKind =
  | "doc.text"
  | "doc.format"
  | "doc.blockType"
  | "doc.insert"
  | "doc.table"
  | "doc.remove"
  | "sheet.cell"
  | "sheet.header"
  | "sheet.row";

export type OfficeStep = {
  id: number;
  index: number;
  kind: StepKind;
  title: string;
  detail: string;
  module: ModuleKey;
  time: string;
  snapshot: OfficeState;
};

// #region state.initial
export const INITIAL_OFFICE: OfficeState = {
  doc: {
    title: "2026 年第四季度工作规划",
    blocks: [
      { id: "b1", type: "h1", text: "2026 年第四季度工作规划" },
      { id: "b2", type: "p", text: "" },
    ],
  },
  sheet: {
    name: "预算与进度",
    cols: 5,
    rows: 7,
    headerBold: false,
    cells: {
      A1: "项目",
      B1: "预算（万）",
      C1: "进度",
      A2: "办公套件重构",
      C2: "进行中",
      A3: "数据看板",
      C3: "未开始",
      A4: "合计",
    },
  },
};
// #endregion state.initial

// #region state.helpers
function withBlockPatch(state: OfficeState, blockId: string, patch: Partial<DocBlock>): OfficeState {
  return {
    ...state,
    doc: {
      ...state.doc,
      blocks: state.doc.blocks.map((block) => (block.id === blockId ? { ...block, ...patch } : block)),
    },
  };
}

function withBlockInsert(state: OfficeState, afterId: string, block: DocBlock): OfficeState {
  const blocks = [...state.doc.blocks];
  const index = blocks.findIndex((item) => item.id === afterId);
  blocks.splice(index < 0 ? blocks.length : index + 1, 0, block);
  return { ...state, doc: { ...state.doc, blocks } };
}

function withBlockRemove(state: OfficeState, blockId: string): OfficeState {
  return {
    ...state,
    doc: { ...state.doc, blocks: state.doc.blocks.filter((block) => block.id !== blockId) },
  };
}

function withCell(state: OfficeState, ref: string, raw: string): OfficeState {
  return { ...state, sheet: { ...state.sheet, cells: { ...state.sheet.cells, [ref]: raw } } };
}
// #endregion state.helpers

const SEED_TEXT = "本季度围绕「稳定、提效、可度量」三条主线推进，所有任务都要有明确的验收口径。";

const SEED_OPERATIONS: {
  kind: StepKind;
  title: string;
  detail: string;
  module: ModuleKey;
  time: string;
  apply: (state: OfficeState) => OfficeState;
}[] = [
  {
    kind: "doc.text",
    title: "输入正文",
    detail: `在 b2 写入 ${SEED_TEXT.length} 个字符的正文段落`,
    module: "doc",
    time: "09:12",
    apply: (state) => withBlockPatch(state, "b2", { text: SEED_TEXT }),
  },
  {
    kind: "doc.table",
    title: "插入表格",
    detail: "在正文之后插入 3×3 的任务表",
    module: "doc",
    time: "09:18",
    apply: (state) =>
      withBlockInsert(state, "b2", {
        id: "b3",
        type: "table",
        text: "",
        rows: [
          ["任务", "时间", "负责人"],
          ["办公套件重构", "9 月", "王宁"],
          ["数据看板", "10 月", "李澜"],
        ],
      }),
  },
  {
    kind: "sheet.cell",
    title: "填写单元格",
    detail: "B2 ← 12（办公套件重构预算）",
    module: "sheet",
    time: "09:24",
    apply: (state) => withCell(state, "B2", "12"),
  },
  {
    kind: "sheet.cell",
    title: "填写单元格",
    detail: "B3 ← 8（数据看板预算）",
    module: "sheet",
    time: "09:25",
    apply: (state) => withCell(state, "B3", "8"),
  },
  {
    kind: "sheet.cell",
    title: "写入公式",
    detail: "B4 ← =SUM(B2:B3)，显示 20",
    module: "sheet",
    time: "09:27",
    apply: (state) => withCell(state, "B4", "=SUM(B2:B3)"),
  },
  {
    kind: "doc.format",
    title: "段落加粗",
    detail: "b2 的 bold 切换为 true",
    module: "doc",
    time: "09:31",
    apply: (state) => withBlockPatch(state, "b2", { bold: true }),
  },
];

function createSeed(): { state: OfficeState; steps: OfficeStep[] } {
  let state = INITIAL_OFFICE;
  const steps: OfficeStep[] = [];

  SEED_OPERATIONS.forEach((operation, index) => {
    state = operation.apply(state);
    steps.push({
      id: index + 1,
      index: index + 1,
      kind: operation.kind,
      title: operation.title,
      detail: operation.detail,
      module: operation.module,
      time: operation.time,
      snapshot: structuredClone(state),
    });
  });

  return { state, steps };
}

const seed = createSeed();

type OfficeContextValue = {
  office: OfficeState;
  view: OfficeState;
  steps: OfficeStep[];
  activeStepId: number | null;
  activeStep: OfficeStep | null;
  replaying: boolean;
  activeBlockId: string;
  activeCell: string;
  module: ModuleKey;
  setModule: (module: ModuleKey) => void;
  selectStep: (id: number | null) => void;
  selectBlock: (id: string) => void;
  selectCell: (ref: string) => void;
  patchText: (blockId: string, text: string) => void;
  commitText: (blockId: string) => void;
  toggleFormat: (blockId: string, prop: "bold" | "italic") => void;
  changeBlockType: (blockId: string, type: BlockType) => void;
  insertParagraph: (afterId: string) => string;
  insertTable: (afterId: string) => void;
  removeBlock: (blockId: string) => void;
  setCellValue: (ref: string, raw: string) => void;
  toggleHeaderBold: () => void;
  addRow: () => void;
};

const OfficeContext = createContext<OfficeContextValue | null>(null);

const TYPE_TITLES: Record<BlockType, string> = {
  h1: "设为标题 1",
  h2: "设为标题 2",
  p: "设为正文",
  li: "转为项目符号",
  table: "插入表格",
};

function clock(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export function OfficeProvider({ children }: { children: ReactNode }) {
  const [office, setOffice] = useState<OfficeState>(seed.state);
  const [steps, setSteps] = useState<OfficeStep[]>(seed.steps);
  const [activeStepId, setActiveStepId] = useState<number | null>(null);
  const [activeBlockId, setActiveBlockId] = useState("b2");
  const [activeCell, setActiveCell] = useState("B2");
  const [module, setModule] = useState<ModuleKey>("doc");

  const officeRef = useRef(office);
  const stepCounter = useRef(seed.steps.length);
  const blockCounter = useRef(seed.steps.length + 10);
  const lastCommitted = useRef<Record<string, string>>(
    Object.fromEntries(seed.state.doc.blocks.map((block) => [block.id, block.text])),
  );

  const apply = (next: OfficeState): void => {
    officeRef.current = next;
    setOffice(next);
  };

  // #region action.record
  const record = (kind: StepKind, title: string, detail: string, module: ModuleKey): void => {
    stepCounter.current += 1;
    const step: OfficeStep = {
      id: stepCounter.current,
      index: stepCounter.current,
      kind,
      title,
      detail,
      module,
      time: clock(),
      snapshot: structuredClone(officeRef.current),
    };
    setSteps((previous) => [...previous, step]);
    setActiveStepId(null);
  };
  // #endregion action.record

  // #region action.text
  const patchText = (blockId: string, text: string): void => {
    apply(withBlockPatch(officeRef.current, blockId, { text }));
  };

  const commitText = (blockId: string): void => {
    const block = officeRef.current.doc.blocks.find((item) => item.id === blockId);
    if (!block || !block.text.trim()) return;
    if (lastCommitted.current[blockId] === block.text) return;
    lastCommitted.current[blockId] = block.text;
    record("doc.text", "输入正文", `${blockId} 更新为 ${block.text.length} 个字符`, "doc");
  };
  // #endregion action.text

  // #region action.format
  const toggleFormat = (blockId: string, prop: "bold" | "italic"): void => {
    const block = officeRef.current.doc.blocks.find((item) => item.id === blockId);
    if (!block) return;
    const nextValue = !block[prop];
    apply(withBlockPatch(officeRef.current, blockId, { [prop]: nextValue }));
    record(
      "doc.format",
      prop === "bold" ? "段落加粗" : "段落倾斜",
      `${blockId} 的 ${prop} 切换为 ${nextValue}`,
      "doc",
    );
  };
  // #endregion action.format

  // #region action.blockType
  const changeBlockType = (blockId: string, type: BlockType): void => {
    const block = officeRef.current.doc.blocks.find((item) => item.id === blockId);
    if (!block || block.type === type || block.type === "table") return;
    apply(withBlockPatch(officeRef.current, blockId, { type }));
    record("doc.blockType", TYPE_TITLES[type], `${blockId} 由 ${block.type} 改为 ${type}`, "doc");
  };
  // #endregion action.blockType

  // #region action.insert
  const insertParagraph = (afterId: string): string => {
    blockCounter.current += 1;
    const block: DocBlock = { id: `b${blockCounter.current}`, type: "p", text: "" };
    apply(withBlockInsert(officeRef.current, afterId, block));
    lastCommitted.current[block.id] = "";
    setActiveBlockId(block.id);
    record("doc.insert", "插入段落", `在 ${afterId} 之后新建 ${block.id}`, "doc");
    return block.id;
  };
  // #endregion action.insert

  // #region action.table
  const insertTable = (afterId: string): void => {
    blockCounter.current += 1;
    const block: DocBlock = {
      id: `b${blockCounter.current}`,
      type: "table",
      text: "",
      rows: [
        ["任务", "口径", "负责人"],
        ["", "", ""],
        ["", "", ""],
      ],
    };
    apply(withBlockInsert(officeRef.current, afterId, block));
    setActiveBlockId(block.id);
    record("doc.table", "插入表格", `在 ${afterId} 之后插入 3×3 表格`, "doc");
  };
  // #endregion action.table

  // #region action.remove
  const removeBlock = (blockId: string): void => {
    const block = officeRef.current.doc.blocks.find((item) => item.id === blockId);
    if (!block || officeRef.current.doc.blocks.length <= 1) return;
    apply(withBlockRemove(officeRef.current, blockId));
    const fallback = officeRef.current.doc.blocks[0];
    setActiveBlockId(fallback ? fallback.id : "");
    record("doc.remove", "删除段落", `移除 ${blockId}（${block.text.slice(0, 12) || "空段落"}）`, "doc");
  };
  // #endregion action.remove

  // #region action.cell
  const setCellValue = (ref: string, raw: string): void => {
    const previous = officeRef.current.sheet.cells[ref] ?? "";
    if (previous === raw) return;
    apply(withCell(officeRef.current, ref, raw));
    const shown = displayCell(officeRef.current.sheet.cells, ref);
    record(
      "sheet.cell",
      raw.trim().startsWith("=") ? "写入公式" : "填写单元格",
      `${ref} ← ${raw || "（清空）"}，显示 ${shown || "空"}`,
      "sheet",
    );
  };
  // #endregion action.cell

  // #region action.header
  const toggleHeaderBold = (): void => {
    const nextValue = !officeRef.current.sheet.headerBold;
    apply({ ...officeRef.current, sheet: { ...officeRef.current.sheet, headerBold: nextValue } });
    record("sheet.header", "表头加粗", `表头字重切换为 ${nextValue ? "bold" : "normal"}`, "sheet");
  };
  // #endregion action.header

  // #region action.row
  const addRow = (): void => {
    const sheet = officeRef.current.sheet;
    apply({ ...officeRef.current, sheet: { ...sheet, rows: sheet.rows + 1 } });
    record("sheet.row", "新增行", `表格扩展为 ${sheet.rows + 1} 行`, "sheet");
  };
  // #endregion action.row

  const selectStep = (id: number | null): void => {
    setActiveStepId(id);
    if (id !== null) {
      const target = steps.find((step) => step.id === id);
      if (target) setModule(target.module);
    }
  };

  const activeStep = steps.length === 0 ? null : activeStepId === null ? steps[steps.length - 1] : steps.find((step) => step.id === activeStepId) ?? null;
  const view = activeStepId === null ? office : activeStep?.snapshot ?? office;

  const value: OfficeContextValue = {
    office,
    view,
    steps,
    activeStepId,
    activeStep,
    replaying: activeStepId !== null,
    activeBlockId,
    activeCell,
    module,
    setModule,
    selectStep,
    selectBlock: setActiveBlockId,
    selectCell: setActiveCell,
    patchText,
    commitText,
    toggleFormat,
    changeBlockType,
    insertParagraph,
    insertTable,
    removeBlock,
    setCellValue,
    toggleHeaderBold,
    addRow,
  };

  return <OfficeContext.Provider value={value}>{children}</OfficeContext.Provider>;
}

export function useOffice(): OfficeContextValue {
  const context = useContext(OfficeContext);
  if (!context) throw new Error("useOffice 必须在 OfficeProvider 内使用");
  return context;
}
